import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, where, getDoc, doc, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { API_URL } from '../utils/apiConfig';
import OppModal, { getValueRangeLabel, getValueRangeMidpoint, formatActualValue } from '../components/forms/OppModal';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import DateRangeSelector from '../components/common/DateRangeSelector';

import OpportunityPipeline from '../components/opportunities/OpportunityPipeline';
import ReEngagementQueue from '../components/opportunities/ReEngagementQueue';
import { Link, useNavigate } from 'react-router-dom';
import { calculateWinProbability } from '../utils/winProbability';
import { exportData } from '../utils/exportUtils';
import ExportButton from '../components/common/ExportButton';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import { importOpportunitiesWithCustomerMatch, generateImportSummary, generateImportTemplate } from '../utils/opportunityImportUtils';
import { logUserAction, logEntityChanges, logEntityDeletion } from '../utils/logger';
import { formatDate } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import PaginationControls from '../components/common/PaginationControls';
import { collectionPath } from '../utils/firestoreCollections';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, ACTION_PERMISSIONS } from '../utils/permissions';
import BulkEditModal from '../components/common/BulkEditModal';
import { useTableState } from '../hooks/useTableState';

import Papa from 'papaparse';
import { addDoc, updateDoc, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';

import { handleFirestoreError, PerformanceMonitor } from '../utils/errorHandling';
import useEnrichedOpportunities from '../hooks/useEnrichedOpportunities';
import { getOpportunityAssignmentUsers } from '../utils/assignmentUsers';

const PAGE_SIZE = 15; // Default page size for pagination

function DropdownCheckbox({ options, selected, onChange, label }) {
    const [open, setOpen] = useState(false);
    const ref = useRef();

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                className="border rounded px-3 py-2 bg-white flex items-center min-w-[12rem]"
                onClick={() => setOpen(o => !o)}
            >
                <span className="mr-2">{label}</span>
                <span className="truncate text-gray-700 text-sm">
                    {selected.length === 0 ? 'All' : selected.join(', ')}
                </span>
                <svg className="ml-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="absolute z-10 mt-1 bg-white border rounded shadow-lg w-full max-h-60 overflow-auto">
                    {options.map(opt => (
                        <label key={opt} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selected.includes(opt)}
                                onChange={e => {
                                    if (e.target.checked) {
                                        onChange([...selected, opt]);
                                    } else {
                                        onChange(selected.filter(s => s !== opt));
                                    }
                                }}
                                className="form-checkbox text-indigo-600 mr-2"
                            />
                            <span className="text-sm">{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

const OppPage = ({ user }) => {
    const { can } = usePermissions(user);
    const [opportunities, setOpportunities] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editOpportunity, setEditOpportunity] = useState(null);
    const [toast, setToast] = useState(null);
    const fb = useFilterBar({
        search: '',
        stageFilter: [],
        range: '90',
        customStart: '',
        customEnd: '',
    });
    const { sortBy, sortDir, page: oppPage, setSort: setTableSort, setPage: setOppPage } = useTableState({ sortBy: 'dueDate', sortDir: 'asc', page: 1 });

    const prevFilterCountRef = useRef(null);

    // Importing state
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef();

    // Bulk edit state (admin only)
    const canBulkEdit = can(ACTION_PERMISSIONS.BULK_OPERATIONS);
    const [selectedOppIds, setSelectedOppIds] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // Clear selection when filters change
    useEffect(() => { setSelectedOppIds(new Set()); }, [fb.applied]);

    // Date range calculation
    const { start, end } = useMemo(() => {
        const now = new Date();
        let start, end;
        if (fb.applied.range === "custom") {
            if (!fb.applied.customStart || !fb.applied.customEnd) {
                return { start: null, end: null };
            }
            start = new Date(fb.applied.customStart);
            end = new Date(fb.applied.customEnd);
            
            // Validate 365-day limit
            const diffMs = end.getTime() - start.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            if (diffDays > 365 || start > end) {
                return { start: null, end: null };
            }
        } else if (fb.applied.range === "ytd") {
            start = new Date(now.getFullYear(), 0, 1); // Jan 1 of current year
            end = now;
        } else {
            end = now;
            start = new Date(now.getTime() - Number(fb.applied.range) * 24 * 60 * 60 * 1000);
        }
        return { start, end };
    }, [fb.applied.range, fb.applied.customStart, fb.applied.customEnd]);


    useEffect(() => {
        if (!user || !user.uid) return;

        let unsubOpportunities = () => {};

        const perfId = PerformanceMonitor.startOperation('load-opportunities');
        let perfEnded = false; // Track if we've already ended the performance operation
        setLoading(true);

        if (can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
            unsubOpportunities = onSnapshot(
                query(
                    collection(db, collectionPath('opportunities')),
                    orderBy('startDate', 'desc'),
                    limit(100) // Limit to most recent opportunities
                ),
                (snapshot) => {
                    setOpportunities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    setLoading(false);
                    // Only end performance operation on first load, not on real-time updates
                    if (!perfEnded) {
                        PerformanceMonitor.endOperation(perfId, true);
                        perfEnded = true;
                    }
                },
                (error) => {
                    if (!perfEnded) {
                        PerformanceMonitor.endOperation(perfId, false);
                        perfEnded = true;
                    }
                    const friendlyMsg = handleFirestoreError(error, 'load-opportunities');
                    setToast({ message: friendlyMsg, type: 'error' });
                    setLoading(false);
                }
            );
        } else {
            // Non-admin: fetch only opportunities assigned to this user
            unsubOpportunities = onSnapshot(
                query(
                    collection(db, collectionPath('opportunities')),
                    where('uid', '==', user.uid),
                    orderBy('startDate', 'desc'),
                    limit(100)
                ),
                (snapshot) => {
                    setOpportunities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    setLoading(false);
                    // Only end performance operation on first load, not on real-time updates
                    if (!perfEnded) {
                        PerformanceMonitor.endOperation(perfId, true);
                        perfEnded = true;
                    }
                },
                (error) => {
                    if (!perfEnded) {
                        PerformanceMonitor.endOperation(perfId, false);
                        perfEnded = true;
                    }
                    const friendlyMsg = handleFirestoreError(error, 'load-opportunities');
                    setToast({ message: friendlyMsg, type: 'error' });
                    setLoading(false);
                }
            );
        }

        return () => {
            unsubOpportunities();
        };
    }, [user, can]);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        // Fetch all users if admin OR if user can see all opps (needs names for assignment display)
        if (can(FEATURE_PERMISSIONS.USER_VIEW_ALL) || can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
            const unsub = onSnapshot(collection(db, collectionPath('users')), (snap) => {
                if (!cancelled) {
                    setUsers(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
                }
            });
            return () => { cancelled = true; unsub(); };
        } else {
            getDoc(doc(db, collectionPath('users'), user.uid)).then(snap => {
                if (!cancelled) {
                    if (snap.exists()) setUsers([{ uid: snap.id, ...snap.data() }]);
                    else setUsers([]);
                }
            });
            return () => { cancelled = true; };
        }
    }, [user, can]);

    const handleSort = (column) => {
        const newDir = sortBy === column ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setTableSort(column, newDir);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this opportunity?')) return;

        const perfId = PerformanceMonitor.startOperation('delete-opportunity');
        try {
            // Get the opportunity data before deleting for logging
            const oppDoc = await getDoc(doc(db, collectionPath('opportunities'), id));
            const oppData = oppDoc.exists() ? oppDoc.data() : null;
            
            // Delete the opportunity
            await deleteDoc(doc(db, collectionPath('opportunities'), id));
            
            // Log the deletion to security logs
            if (oppData) {
                logEntityDeletion({
                    user,
                    entityType: 'Opportunity',
                    entityId: id,
                    entityName: oppData.opportunityNumberLabel || oppData.leadSource || id,
                    associations: { customerId: oppData.customerId }
                }).catch(e => console.warn('Failed to log deletion:', e));
            }
            
            // Delete associated notes and tasks from unified collections
            const { query, where, collection } = await import('firebase/firestore');
            const notesQuery = query(
                collection(db, collectionPath('notes')),
                where('associations.opportunityId', '==', id)
            );
            const tasksQuery = query(
                collection(db, collectionPath('tasks')),
                where('associations.opportunityId', '==', id)
            );
            const notifsQuery = query(
                collection(db, collectionPath('notifications')),
                where('related.type', '==', 'Opportunity'),
                where('related.id', '==', id)
            );
            const [notesSnapshot, tasksSnapshot, notifsSnapshot] = await Promise.all([
                getDocs(notesQuery),
                getDocs(tasksQuery),
                getDocs(notifsQuery)
            ]);
            
            // Delete all notes, tasks, and notifications for this opportunity
            const deletePromises = [
                ...notesSnapshot.docs.map(noteDoc => 
                    deleteDoc(doc(db, collectionPath('notes'), noteDoc.id))
                ),
                ...tasksSnapshot.docs.map(taskDoc => 
                    deleteDoc(doc(db, collectionPath('tasks'), taskDoc.id))
                ),
                ...notifsSnapshot.docs.map(nDoc =>
                    deleteDoc(doc(db, collectionPath('notifications'), nDoc.id))
                )
            ];
            await Promise.all(deletePromises);

            // Log cascade deletions
            const cascadeCounts = {
                notes: notesSnapshot.docs.length,
                tasks: tasksSnapshot.docs.length,
                notifications: notifsSnapshot.docs.length
            };
            if (cascadeCounts.notes + cascadeCounts.tasks + cascadeCounts.notifications > 0) {
                const parts = [];
                if (cascadeCounts.notes) parts.push(`${cascadeCounts.notes} note${cascadeCounts.notes !== 1 ? 's' : ''}`);
                if (cascadeCounts.tasks) parts.push(`${cascadeCounts.tasks} task${cascadeCounts.tasks !== 1 ? 's' : ''}`);
                if (cascadeCounts.notifications) parts.push(`${cascadeCounts.notifications} notification${cascadeCounts.notifications !== 1 ? 's' : ''}`);
                logUserAction({
                    action: 'Cascade Deleted',
                    details: `Deleted ${parts.join(', ')} associated with opportunity ${oppData?.opportunityNumberLabel || id}`,
                    entityType: 'Opportunity',
                    entityId: id,
                    customerId: oppData?.customerId || null,
                    level: 'info'
                }).catch(e => console.warn('Failed to log cascade deletion:', e));
            }
            
            PerformanceMonitor.endOperation(perfId, true);
            setToast({ message: 'Opportunity and associated notes/tasks deleted successfully', type: 'success' });
        } catch (err) {
            PerformanceMonitor.endOperation(perfId, false);
            const friendlyMsg = handleFirestoreError(err, 'delete-opportunity');
            setToast({ message: friendlyMsg, type: 'error' });
        }
    };

    const handleBulkDelete = async () => {
        const count = selectedOppIds.size;
        if (!window.confirm(`Are you sure you want to delete ${count} opportunit${count !== 1 ? 'ies' : 'y'}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const ids = [...selectedOppIds];
            // Delete opportunities in batches
            const BATCH_SIZE = 400;
            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                const chunk = ids.slice(i, i + BATCH_SIZE);
                const batch = writeBatch(db);
                for (const id of chunk) {
                    batch.delete(doc(db, collectionPath('opportunities'), id));
                }
                await batch.commit();
            }
            // Log deletions and clean up associated notes (fire-and-forget)
            for (const id of ids) {
                const opp = opportunities.find(o => o.id === id);
                if (opp) {
                    logEntityDeletion({
                        user,
                        entityType: 'Opportunity',
                        entityId: id,
                        entityName: opp.opportunityNumberLabel || opp.leadSource || id,
                        associations: { customerId: opp.customerId },
                    }).catch(() => {});
                }
                // Delete associated notes, tasks, and notifications
                const { query: q, where: w, collection: coll } = await import('firebase/firestore');
                const notesQ = q(coll(db, collectionPath('notes')), w('associations.opportunityId', '==', id));
                const tasksQ = q(coll(db, collectionPath('tasks')), w('associations.opportunityId', '==', id));
                const notifsQ = q(coll(db, collectionPath('notifications')), w('related.type', '==', 'Opportunity'), w('related.id', '==', id));
                const [notesSnap, tasksSnap, notifsSnap] = await Promise.all([getDocs(notesQ), getDocs(tasksQ), getDocs(notifsQ)]);
                notesSnap.docs.forEach(noteDoc => deleteDoc(doc(db, collectionPath('notes'), noteDoc.id)).catch(() => {}));
                tasksSnap.docs.forEach(taskDoc => deleteDoc(doc(db, collectionPath('tasks'), taskDoc.id)).catch(() => {}));
                notifsSnap.docs.forEach(nDoc => deleteDoc(doc(db, collectionPath('notifications'), nDoc.id)).catch(() => {}));
                // Log cascade deletions
                const cNotes = notesSnap.docs.length, cTasks = tasksSnap.docs.length, cNotifs = notifsSnap.docs.length;
                if (cNotes + cTasks + cNotifs > 0) {
                    const parts = [];
                    if (cNotes) parts.push(`${cNotes} note${cNotes !== 1 ? 's' : ''}`);
                    if (cTasks) parts.push(`${cTasks} task${cTasks !== 1 ? 's' : ''}`);
                    if (cNotifs) parts.push(`${cNotifs} notification${cNotifs !== 1 ? 's' : ''}`);
                    logUserAction({
                        action: 'Cascade Deleted',
                        details: `Deleted ${parts.join(', ')} associated with opportunity ${opp?.opportunityNumberLabel || id}`,
                        entityType: 'Opportunity',
                        entityId: id,
                        customerId: opp?.customerId || null,
                        level: 'info'
                    }).catch(() => {});
                }
            }
            setSelectedOppIds(new Set());
            setToast({ type: 'success', message: `Deleted ${count} opportunit${count !== 1 ? 'ies' : 'y'}.` });
        } catch (err) {
            console.error('Bulk delete error:', err);
            setToast({ type: 'error', message: `Failed to delete: ${err.message}` });
        } finally {
            setBulkDeleting(false);
        }
    };


    const { currentUser } = useAuth();

    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach(u => m.set(u.uid, u));
        return m;
    }, [users]);

    const searchLower = (fb.applied.search || '').toLowerCase().trim();

    const filteredOpportunities = useMemo(() => {
        return opportunities.filter(opp => {
            // Admins see all opportunities
            if (can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) return true;

            // Non-admins only see opportunities assigned directly to them
            if (String(opp.uid) === String(user?.uid) || String(opp.uid) === String(user?.id)) return true;

            // Otherwise, hide
            return false;
        })
        // Apply stage, date, and search filters
        .filter(opp => {
            if (fb.applied.stageFilter.length > 0 && !fb.applied.stageFilter.includes(opp.stage)) return false;
            
            // Skip date filtering if invalid/missing custom dates
            if (start && end) {
                // Handle Firestore Timestamps - they have a toDate() method
                let oppDateRaw = opp.startDate || opp.date;
                let oppDate = null;
                if (oppDateRaw) {
                    if (typeof oppDateRaw.toDate === 'function') {
                        // Firestore Timestamp
                        oppDate = oppDateRaw.toDate();
                    } else if (oppDateRaw.seconds) {
                        // Timestamp-like object with seconds
                        oppDate = new Date(oppDateRaw.seconds * 1000);
                    } else {
                        // String or Date
                        oppDate = new Date(oppDateRaw);
                    }
                }
                if (oppDate && !isNaN(oppDate)) {
                    // Set time to start of day for proper comparison
                    oppDate.setHours(0, 0, 0, 0);
                    const startOfDay = new Date(start);
                    startOfDay.setHours(0, 0, 0, 0);
                    const endOfDay = new Date(end);
                    endOfDay.setHours(23, 59, 59, 999);
                    if (oppDate < startOfDay) return false;
                    if (oppDate > endOfDay) return false;
                }
            }
            
            if (!searchLower) return true;
            const rep = usersById.get(opp.uid);
            const searchable = [
                opp.leadSource,
                opp.stage,
                opp.value?.toString(),
                opp.startDate,
                opp.dueDate,
                opp.customerName,
                rep
                    ? (rep.name || `${rep.firstName || ''} ${rep.lastName || ''}` || rep.displayName || rep.email)
                    : 'unassigned',
                opp.leadSource,
            ].filter(Boolean).map(x => x.toString().toLowerCase());
            return searchable.some(v => v.includes(searchLower));
        });
    }, [opportunities, users, user, searchLower, fb.applied.stageFilter, start, end, usersById]);

    const sortedOpportunities = useMemo(() => {
        const sorted = [...filteredOpportunities];
        sorted.sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];

            if (sortBy === 'customer') {
                aValue = a.customerName || '';
                bValue = b.customerName || '';
            } else if (sortBy === 'assignedUser') {
                const aRep = usersById.get(a.uid);
                const bRep = usersById.get(b.uid);
                aValue = aRep?.name || aRep?.displayName || aRep?.email || '';
                bValue = bRep?.name || bRep?.displayName || bRep?.email || '';
            } else if (sortBy === 'value') {
                aValue = getValueRangeMidpoint(a.value, a.actualValue);
                bValue = getValueRangeMidpoint(b.value, b.actualValue);
            } else if (sortBy === 'winProbability') {
                // Calculate win probability dynamically for both opportunities
                aValue = calculateWinProbability(a.stage, a.leadSource, a.daysOpen) || 0;
                bValue = calculateWinProbability(b.stage, b.leadSource, b.daysOpen) || 0;
            } else if (sortBy === 'startDate' || sortBy === 'dueDate') {
                // Handle Firestore Timestamps and date strings
                if (aValue) {
                    aValue = (typeof aValue.toDate === 'function') ? aValue.toDate().getTime() : new Date(aValue).getTime();
                } else {
                    aValue = 0;
                }
                if (bValue) {
                    bValue = (typeof bValue.toDate === 'function') ? bValue.toDate().getTime() : new Date(bValue).getTime();
                } else {
                    bValue = 0;
                }
            } else {
                aValue = (aValue ?? '').toString();
                bValue = (bValue ?? '').toString();
            }

            if (typeof aValue === 'string' || typeof bValue === 'string') {
                return sortDir === 'asc'
                    ? aValue.toString().localeCompare(bValue.toString())
                    : bValue.toString().localeCompare(aValue.toString());
            }
            return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
        });
        return sorted;
    }, [filteredOpportunities, sortBy, sortDir, usersById]);

    // Pagination calculations (add after sortedOpportunities)
    const oppTotalPages = Math.max(1, Math.ceil(sortedOpportunities.length / PAGE_SIZE));
    const clampedOppPage = Math.min(oppPage, oppTotalPages);
    const paginatedOpportunities = useMemo(() => {
        const startIdx = (clampedOppPage - 1) * PAGE_SIZE;
        return sortedOpportunities.slice(startIdx, startIdx + PAGE_SIZE);
    }, [sortedOpportunities, clampedOppPage]);

    // Reset to page 1 only when filters actually change (not on initial data load)
    const hasInitialLoadRef = useRef(false);
    useEffect(() => {
        if (!hasInitialLoadRef.current) {
            // First time we get data — don't reset, just mark as loaded
            if (filteredOpportunities.length > 0) {
                hasInitialLoadRef.current = true;
                prevFilterCountRef.current = filteredOpportunities.length;
            }
            return;
        }
        if (filteredOpportunities.length !== prevFilterCountRef.current) {
            setOppPage(1);
        }
        prevFilterCountRef.current = filteredOpportunities.length;
    }, [filteredOpportunities.length]);

    // Opportunities with sales rep names for the pipeline board (shared hook)
    const opportunitiesWithNames = useEnrichedOpportunities(filteredOpportunities, { users });

    const navigate = useNavigate();

    // Export opportunities in multiple formats
    const handleExport = (format) => {
        const headers = [
            "Opportunity Number", "Stage", "Customer", "Contact Name", "Contact Email", "Lead Source", "Estimated Value", "Actual Value", "Start Date", "Due Date", "Sales Rep"
        ];
        
        const rowMapper = o => {
            const salesRep = usersById.get(o.uid);
            
            // Format dates using formatDate utility with UTC methods
            const startDateStr = o.startDate 
                ? (o.startDate.toDate ? formatDate(o.startDate.toDate(), 'MM/DD/YYYY') : formatDate(new Date(o.startDate), 'MM/DD/YYYY'))
                : (o.date ? (o.date.toDate ? formatDate(o.date.toDate(), 'MM/DD/YYYY') : formatDate(new Date(o.date), 'MM/DD/YYYY')) : '');
            
            const dueDateStr = o.dueDate
                ? (o.dueDate.toDate ? formatDate(o.dueDate.toDate(), 'MM/DD/YYYY') : formatDate(new Date(o.dueDate), 'MM/DD/YYYY'))
                : '';
            
            return [
                o.opportunityNumber || '',
                o.stage || '',
                o.customerName || '',
                o.contactName || '',
                o.contactEmail || '',
                o.leadSource || '',
                getValueRangeLabel(o.value),
                o.actualValue ? formatActualValue(o.actualValue) : '',
                startDateStr,
                dueDateStr,
                salesRep?.displayName || salesRep?.name || salesRep?.email || o.salesReps?.[0]?.salesRepName || o.salesRepNames?.[0] || 'Unassigned'
            ];
        };
        
        exportData({
            filename: 'opportunities',
            format,
            headers,
            data: sortedOpportunities,
            rowMapper,
            options: format.toLowerCase() === 'pdf' ? {
                title: `Opportunities Export - ${new Date().toLocaleDateString()}`,
                fontSize: 8,
                orientation: 'landscape',
                pageSize: 'a4'
            } : {
                title: `Opportunities Export - ${new Date().toLocaleDateString()}`,
                sheetName: 'Opportunities',
                columnWidths: [15, 15, 25, 20, 25, 15, 12, 12, 12, 20]
            }
        });
    };

    // Smart Import with Customer Matching
    const handleSmartImportCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setError('');

        try {
            // Parse CSV
            const text = await file.text();
            const parsed = Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                transformHeader: (header) => header.trim()
            });

            if (parsed.errors && parsed.errors.length > 0) {
                throw new Error(`CSV parsing error: ${parsed.errors[0].message}`);
            }

            const csvData = parsed.data;

            // Filter out template example/documentation rows before showing count
            const importRows = csvData.filter(row => {
                const customerName = (row['Customer Name'] || row['Company'] || row['Business'] || row['Account'] || row['Organization'] || row['Brand'] || row['Club'] || '').trim();
                if (!customerName) return false;
                if (customerName.startsWith('---') || 
                    (customerName.toUpperCase().includes('VALID') && customerName.toUpperCase().includes('VALUES'))) return false;
                const stage = (row['Stage'] || row['Status'] || '').trim();
                if (stage.includes('New') && stage.includes('Qualified') && stage.includes('Closed Won') && stage.includes('Closed Lost')) return false;
                return true;
            });

            if (importRows.length === 0) {
                throw new Error('CSV file contains no importable data rows (only template examples/documentation)');
            }

            // Show confirmation
            const confirmed = window.confirm(
                `📥 Smart Import\n\n` +
                `This will import ${importRows.length} opportunit${importRows.length === 1 ? 'y' : 'ies'} with intelligent customer matching:\n\n` +
                `✅ Auto-match existing customers (fuzzy matching)\n` +
                `✅ Create placeholder customers for new leads\n` +
                `✅ Flag new customers for review\n` +
                `✅ Skip duplicate opportunities\n\n` +
                `Continue?`
            );

            if (!confirmed) {
                setImporting(false);
                if (importInputRef.current) importInputRef.current.value = '';
                return;
            }

            // Fetch ALL customers for matching (not just the limited set from state)
            console.log('Fetching all customers for import matching...');
            let allCustomers = [];
            try {
                const customersSnap = await getDocs(
                    query(
                        collection(db, collectionPath('customers')),
                        orderBy('customerName', 'asc')
                    )
                );
                allCustomers = customersSnap.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data() 
                }));
                console.log(`Loaded ${allCustomers.length} customers for matching`);
            } catch (err) {
                console.error('Error fetching customers:', err);
                throw new Error(`Failed to load customers for matching: ${err.message}`);
            }

            // Fetch ALL opportunities for duplicate detection (not just the limited set from state)
            console.log('Fetching all opportunities for duplicate detection...');
            let allOpportunities = [];
            try {
                const oppsSnap = await getDocs(
                    query(
                        collection(db, collectionPath('opportunities')),
                        orderBy('startDate', 'desc')
                    )
                );
                allOpportunities = oppsSnap.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data() 
                }));
                console.log(`Loaded ${allOpportunities.length} opportunities for duplicate detection`);
            } catch (err) {
                console.error('Error fetching opportunities:', err);
                throw new Error(`Failed to load opportunities for duplicate detection: ${err.message}`);
            }

            // Perform import
            const results = await importOpportunitiesWithCustomerMatch(
                csvData,
                allCustomers,  // Use ALL customers, not just state
                allOpportunities, // Use ALL opportunities for duplicate detection
                getOpportunityAssignmentUsers(users),
                user,
                (current, total, status) => {
                    // Optional: Update progress UI
                    console.log(`Progress: ${current}/${total} - ${status}`);
                }
            );

            // Show results
            const summary = generateImportSummary(results);
            let message = `Import Complete!\n\n${summary}`;

            if (results.errors.length > 0) {
                message += `\n\n❌ ${results.errors.length} Errors:\n`;
                results.errors.slice(0, 5).forEach(err => {
                    // Handle both string errors and object errors
                    if (typeof err === 'string') {
                        message += `- ${err}\n`;
                    } else if (err.row !== undefined) {
                        message += `- Row ${err.row}: ${err.error}\n`;
                    } else {
                        message += `- ${err.error || err.message || String(err)}\n`;
                    }
                });
                if (results.errors.length > 5) {
                    message += `... and ${results.errors.length - 5} more errors`;
                }
            }

            if (results.warnings.length > 0) {
                // Separate match notifications from review notices
                const matchWarnings = results.warnings.filter(w => w.isMatch || (!w.isReviewNotice && !w.isMerged));
                const reviewNotices = results.warnings.filter(w => w.isReviewNotice);
                
                if (matchWarnings.length > 0) {
                    message += `\n\n✅ Auto-Matched (${matchWarnings.length} total):\n`;
                    matchWarnings.forEach(warn => {
                        message += `- ${warn.message}\n`;
                    });
                }
                
                if (reviewNotices.length > 0) {
                    reviewNotices.forEach(notice => {
                        message += `\n\n${notice.message}\n`;
                    });
                }
            }

            if (results.customersCreated > 0) {
                message += `\n\n🔍 Review Dashboard:\n`;
                message += `${results.customersCreated} new customer${results.customersCreated > 1 ? 's were' : ' was'} created and flagged for review.\n`;
                // Show which rows need review with clear formatting
                if (results.rowsNeedingReview && results.rowsNeedingReview.length > 0) {
                    const rowNumbers = results.rowsNeedingReview.slice(0, 10).map(r => `#${r}`);
                    const rowList = rowNumbers.join(', ');
                    const moreCount = results.rowsNeedingReview.length > 10 ? ` (+${results.rowsNeedingReview.length - 10} more)` : '';
                    message += `CSV row${results.rowsNeedingReview.length > 1 ? 's' : ''}: ${rowList}${moreCount}\n`;
                }
                message += `Go to Customers page to review and match with ApparelMagic customers (if applicable).`;
            }

            alert(message);

            // Log the import (use clean user object to avoid serialization issues)
            await logUserAction({
                user: {
                    uid: user?.uid || '',
                    email: user?.email || '',
                    displayName: user?.displayName || '',
                    role: user?.role || 'user'
                },
                actionType: 'bulk_import',
                entityType: 'Opportunity',
                details: `Smart imported ${results.added} opportunities, created ${results.customersCreated} customers, matched ${results.customersMatched} customers`,
                metadata: {
                    fileName: file.name,
                    rowCount: csvData.length,
                    results: results
                }
            });

            // Send email summary to user (don't set toast here - let email result set it)
            try {
                console.log('Sending import summary email to:', user.email);
                const emailResponse = await fetch(`${API_URL}/api/send-import-summary`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: user.uid,
                        userEmail: user.email,
                        userName: user.displayName || user.email,
                        fileName: file.name,
                        results: results
                    })
                });

                if (!emailResponse.ok) {
                    const errorData = await emailResponse.json().catch(() => ({ error: 'Unknown error' }));
                    console.error('Failed to send import summary email:', errorData);
                    console.error('Email response status:', emailResponse.status, emailResponse.statusText);
                    
                    // Show user-friendly toast notification instead of blocking the import
                    setToast({
                        message: `⚠️ Import completed successfully, but email notification failed. Check server logs for details.`,
                        type: 'warning'
                    });
                } else {
                    console.log('Import summary email sent successfully');
                    setToast({
                        message: `✅ Import completed! Summary email sent to ${user.email}`,
                        type: 'success'
                    });
                }
            } catch (emailErr) {
                console.error('Error sending import summary email:', emailErr);
                // Non-critical error - don't block the import flow
                setToast({
                    message: `⚠️ Import completed successfully, but email notification failed.`,
                    type: 'warning'
                });
            }

            // Refetch opportunities and customers after import so next import has fresh data
            console.log('Refetching opportunities and customers after import...');
            try {
                // Refetch opportunities
                const oppsSnap = await getDocs(
                    query(
                        collection(db, collectionPath('opportunities')),
                        orderBy('startDate', 'desc'),
                        limit(100)
                    )
                );
                setOpportunities(oppsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // Refetch customers
                const customersSnap = await getDocs(
                    query(
                        collection(db, collectionPath('customers')),
                        orderBy('customerName', 'asc'),
                        limit(100)
                    )
                );
                // customers no longer fetched - customerName is denormalized on opportunities
                
                console.log('Refetch complete - ready for next import');
            } catch (refetchErr) {
                console.error('Error refetching after import:', refetchErr);
                // Non-critical error, don't show to user
            }

        } catch (err) {
            console.error('Smart import error:', err);
            let errorMessage = 'Smart import failed';
            if (err.code === 'permission-denied') {
                errorMessage = 'Permission denied. Please check your access rights or contact an administrator.';
            } else if (err.message) {
                errorMessage += ': ' + err.message;
            }
            alert(errorMessage);
        }

        setImporting(false);
        if (importInputRef.current) importInputRef.current.value = '';
    };

    // Template download for smart import
    const handleDownloadSmartTemplate = () => {
        const templateData = generateImportTemplate();
        exportData({
            filename: 'opportunity_smart_import_template',
            format: 'csv',
            headers: templateData[0],
            data: templateData.slice(1),
            rowMapper: (row) => row
        });
    };

    // Show message when custom range selected but dates not entered
    const showCustomRangePrompt = fb.applied.range === 'custom' && (!fb.applied.customStart || !fb.applied.customEnd);

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            {showModal && (
                <OppModal
                    user={user}
                    initial={editOpportunity} // selectedOpportunity should include opportunityNumber
                    onClose={() => { setShowModal(false); setEditOpportunity(null); }}
                    onSaved={(updatedOpp) => {
                        // Close modal
                        setShowModal(false);
                        setEditOpportunity(null);
                        // If we got an updated opportunity object, merge into local state for immediate UI update
                        if (updatedOpp && updatedOpp.id) {
                            setOpportunities(prev => {
                                const found = prev.find(p => p.id === updatedOpp.id);
                                if (found) {
                                    return prev.map(p => p.id === updatedOpp.id ? { ...p, ...updatedOpp } : p);
                                }
                                return [updatedOpp, ...prev];
                            });
                            setToast({ message: `Opportunity ${editOpportunity ? 'updated' : 'added'} successfully`, type: 'success' });
                        } else {
                            setToast({ message: 'Opportunity saved', type: 'success' });
                        }
                    }}
                     users={users}
                 />
             )}

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Opportunities</h2>
            </div>

            {/* Date Range Selector UI */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 space-y-2 md:space-y-0">
                <div className="flex items-center w-full md:w-auto">
                    <span className="mr-2 font-medium text-gray-700">Search:</span>
                    <input
                        type="text"
                        placeholder="Search by customer, lead source, etc..."
                        className={`border rounded px-3 py-2 w-full md:w-[24rem] transition-colors duration-150 ${
                            fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                        }`}
                        value={fb.draft.search}
                        onChange={e => fb.setDraft('search', e.target.value)}
                        onKeyDown={fb.applyOnEnter}
                    />
                </div>
                <DropdownCheckbox
                    options={["New", "Qualified", "Not Qualified", "Proposal Sent", "Closed Won", "Closed Lost"]}
                    selected={fb.draft.stageFilter}
                    onChange={v => fb.setDraft('stageFilter', v)}
                    label="Stage:"
                />
                <DateRangeSelector
                    range={fb.draft.range}
                    setRange={v => fb.setDraft('range', v)}
                    customStart={fb.draft.customStart}
                    setCustomStart={v => fb.setDraft('customStart', v)}
                    customEnd={fb.draft.customEnd}
                    setCustomEnd={v => fb.setDraft('customEnd', v)}
                    defaultRange="90"
                    hideClear
                />
                <FilterActions
                    onApply={fb.apply}
                    onClear={fb.clear}
                    isDirty={fb.isDirty}
                    hasActiveFilters={fb.hasActiveFilters}
                />
            </div>

            {/* Custom range prompt */}
            {showCustomRangePrompt && (
                <div className="mb-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-center">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-yellow-800">
                        Please select start and end dates to view custom range data
                    </span>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center mb-4 space-x-2 flex-wrap gap-2">
                {can(FEATURE_PERMISSIONS.OPP_CREATE) && (
                <Tooltip text="Create a new sales opportunity and track it through the pipeline">
                    <button
                        onClick={() => { setEditOpportunity(null); setShowModal(true); }}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500"
                    >
                        Add New Opportunity
                    </button>
                </Tooltip>
                )}
                {canBulkEdit && selectedOppIds.size > 0 && (
                    <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded px-3 py-1.5">
                        <span className="text-sm font-medium text-indigo-700">{selectedOppIds.size} selected</span>
                        <button
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            onClick={() => setShowBulkEditModal(true)}
                            disabled={bulkDeleting}
                        >
                            Bulk Edit
                        </button>
                        {can(FEATURE_PERMISSIONS.OPP_DELETE) && (
                        <button
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            onClick={handleBulkDelete}
                            disabled={bulkDeleting}
                        >
                            {bulkDeleting ? 'Deleting...' : 'Bulk Delete'}
                        </button>
                        )}
                        <button
                            className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
                            onClick={() => setSelectedOppIds(new Set())}
                            disabled={bulkDeleting}
                        >
                            Clear
                        </button>
                    </div>
                )}
                {can(FEATURE_PERMISSIONS.OPP_EXPORT) && (
                <ExportButton
                    onExport={handleExport}
                    formats={['csv', 'xlsx', 'pdf']}
                    disabled={importing}
                    tooltipText="Export opportunities in various formats (CSV, Excel, PDF)"
                    label="Export"
                    className="bg-green-600 hover:bg-green-700"
                />
                )}
                
                <Tooltip text="Download CSV template with all required columns for importing opportunities">
                    <button
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                        onClick={handleDownloadSmartTemplate}
                        disabled={importing}
                    >
                        Download Smart Template
                    </button>
                </Tooltip>
                
                {/* Smart Import Button */}
                {can(FEATURE_PERMISSIONS.OPP_IMPORT) && (
                <Tooltip text={
                    <div className="text-left">
                        <div className="font-semibold text-purple-300 mb-1">✨ Smart Mode:</div>
                        <div className="text-sm">Auto-matches customers using fuzzy search, creates placeholder customers for new leads, and flags them for review.</div>
                        <div className="text-sm text-purple-200 mt-1">Perfect for bulk opportunity imports!</div>
                    </div>
                } maxWidth={350}>
                    <div className="relative group">
                        <label
                            className={`px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-500 cursor-pointer ${importing ? 'pointer-events-none opacity-50' : ''}`}
                            tabIndex={0}
                        >
                            🎯 Smart Import
                            <input
                                ref={importInputRef}
                                type="file"
                                accept=".csv"
                                onClick={(e) => { e.target.value = null; }}
                                onChange={handleSmartImportCSV}
                                style={{ display: 'none' }}
                                disabled={importing}
                            />
                        </label>
                    </div>
                </Tooltip>
                )}
                
                {/* Legacy Safe Import and Destructive Import buttons removed - replaced by Smart Import */}
            </div>

            {error && paginatedOpportunities.length === 0 && (
                <p className="text-red-500 mb-4">{error}</p>
            )}

            {!showCustomRangePrompt && (
                <>
                    <div className="bg-white rounded-lg shadow">
                        <div className="overflow-x-auto">
                            <table className="w-full whitespace-no-wrap">
                        <thead>
                            <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                {canBulkEdit && (
                                <th className="px-2 py-3 w-8">
                                    <input
                                        type="checkbox"
                                        checked={paginatedOpportunities.length > 0 && paginatedOpportunities.every(o => selectedOppIds.has(o.id))}
                                        onChange={() => {
                                            const pageIds = paginatedOpportunities.map(o => o.id);
                                            const allSelected = pageIds.every(id => selectedOppIds.has(id));
                                            setSelectedOppIds(prev => {
                                                const next = new Set(prev);
                                                pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                                                return next;
                                            });
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        title="Select all on this page"
                                    />
                                </th>
                                )}
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('opportunityNumberLabel')}
                                >
                                    Opportunity Number {sortBy === 'opportunityNumberLabel' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('stage')}
                                >
                                    Stage {sortBy === 'stage' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('customer')}
                                >
                                    Customer {sortBy === 'customer' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('contactName')}
                                >
                                    Contact Name {sortBy === 'contactName' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('contactEmail')}
                                >
                                    Contact Email {sortBy === 'contactEmail' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('leadSource')}
                                >
                                    Lead Source {sortBy === 'leadSource' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('value')}
                                >
                                    Value {sortBy === 'value' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('startDate')}
                                >
                                    Start Date {sortBy === 'startDate' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('dueDate')}
                                >
                                    Due Date {sortBy === 'dueDate' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th
                                    className="px-4 py-3 cursor-pointer"
                                    onClick={() => handleSort('assignedUser')}
                                >
                                    Sales Rep {sortBy === 'assignedUser' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('winProbability')}>
                                    Win Probability (%) {sortBy === 'winProbability' && (sortDir === 'asc' ? '▲' : '▼')}
                                </th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y">
                            {loading && (
                                <tr>
                                    <td colSpan={canBulkEdit ? 12 : 11} className="text-center py-4">Loading opportunities...</td>
                                </tr>
                            )}
                            {!loading && paginatedOpportunities.length === 0 && (
                                <tr>
                                    <td colSpan={canBulkEdit ? 12 : 11} className="text-center py-4">No opportunities found.</td>
                                </tr>
                            )}
                            {!loading && paginatedOpportunities.map(opp => {
                                const assignedUser = users.find(u => u.uid === opp.uid);
                                const salesRepDisplay = assignedUser
                                    ? (
                                        assignedUser.firstName || assignedUser.lastName
                                          ? `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim()
                                          : assignedUser.displayName || assignedUser.email || assignedUser.uid
                                      )
                                    : (opp.salesReps?.[0]?.salesRepName || opp.salesRepNames?.[0] || 'Unassigned');
                                return (
                                    <tr key={opp.id} className={`text-gray-700 hover:bg-gray-50 ${selectedOppIds.has(opp.id) ? 'bg-indigo-50' : ''}`}>
                                        {canBulkEdit && (
                                        <td className="px-2 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedOppIds.has(opp.id)}
                                                onChange={() => setSelectedOppIds(prev => {
                                                    const next = new Set(prev);
                                                    next.has(opp.id) ? next.delete(opp.id) : next.add(opp.id);
                                                    return next;
                                                })}
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                        </td>
                                        )}
                                        <td className="px-4 py-3 text-sm font-mono">
                                            <Link
                                                to={`/opportunity/${opp.id}`}
                                                className="text-indigo-600 hover:underline"
                                            >
                                                {opp.opportunityNumberLabel || '-'}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-base">
                                            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                                opp.stage === 'New' ? 'bg-teal-100 text-teal-700' :
                                                opp.stage === 'Qualified' ? 'bg-gray-100 text-purple-700' :
                                                opp.stage === 'Not Qualified' ? 'bg-gray-100 text-gray-600' :                                                
                                                opp.stage === 'Proposal Sent' ? 'bg-gray-100 text-yellow-700' :
                                                opp.stage === 'Closed Won' ? 'bg-gray-100 text-green-700' :
                                                opp.stage === 'Closed Lost' ? 'bg-gray-100 text-red-700' :
                                                'bg-blue-100 text-blue-700'
                                            }`}>
                                                {opp.stage}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {opp.customerName ? (
                                                can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) ? (
                                                    <Link
                                                        to={`/customer/${opp.customerId}`}
                                                        className="text-indigo-600 hover:underline"
                                                    >
                                                        {opp.customerName}
                                                    </Link>
                                                ) : (
                                                    <span className="text-gray-900" title="You don't have access to view this customer's details">
                                                        {opp.customerName}
                                                    </span>
                                                )
                                            ) : 'Unknown Customer'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{opp.contactName || '-'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {opp.contactEmail ? (
                                                <a href={`mailto:${opp.contactEmail}`} className="text-indigo-600 hover:underline">
                                                    {opp.contactEmail}
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{opp.leadSource || '-'}</td>
                                        <td className="px-4 py-3 text-sm font-medium">
                                            {opp.actualValue ? (
                                                <span title={`Estimated: ${getValueRangeLabel(opp.value)}`}>
                                                    {formatActualValue(opp.actualValue)}
                                                </span>
                                            ) : (
                                                getValueRangeLabel(opp.value)
                                            )}
                                        </td>
                                                                                <td className="px-4 py-3 text-sm">
                                                                                    {opp.startDate ? formatDate(opp.startDate, currentUser?.dateFormat) : '-'}
                                                                                </td>
                                        <td
                                            className={`px-4 py-3 text-sm ${
                                                isPastOrToday(opp.dueDate) && opp.stage !== 'Closed Won' && opp.stage !== 'Closed Lost' ? 'font-bold text-red-700' : ''
                                            }`}
                                        >
                                          {opp.dueDate ? formatDate(opp.dueDate, currentUser?.dateFormat) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                          {salesRepDisplay}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-semibold">
                                            {calculateWinProbability(
                                                opp.stage,
                                                opp.engagement // should be an array of engagement keys
                                            )}%
                                        </td>
                                        <td className="px-4 py-3 space-x-2">
                                            {can(FEATURE_PERMISSIONS.OPP_EDIT) && (
                                            <>
                                            <button
                                                onClick={() => {
                                                    setEditOpportunity(opp);
                                                    setShowModal(true);
                                                }}
                                                className="text-indigo-600 hover:underline"
                                            >
                                                Edit
                                            </button>
                                            {can(FEATURE_PERMISSIONS.OPP_DELETE) && (
                                                <button
                                                    onClick={() => handleDelete(opp.id)}
                                                    className="text-red-600 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                            </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {/* Pagination controls */}
                    <PaginationControls
                        currentPage={clampedOppPage}
                        totalPages={oppTotalPages}
                        onPageChange={setOppPage}
                    />
                </div>
            </div>

            {/* Opportunity Pipeline Board */}
            <div className="mt-12">
                <div className="flex items-center mb-4">
                    <h2 className="text-2xl font-bold mr-2">Opportunity Pipeline</h2>
                    <Tooltip text={
                        <div className="w-full">
                            <div className="font-semibold mb-2 text-center">How to use the Pipeline Board</div>
                            <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1">
                                <div>• <strong>Drag & Drop:</strong> Move cards between columns</div>
                                <div>• <strong>Click to View:</strong> Click cards for details</div>
                                <div>• <strong>Color Coding:</strong> Backgrounds show progression</div>
                                <div>• <strong>Due Dates:</strong> Overdue items in <span className="text-red-300 font-semibold">red</span></div>
                            </div>
                        </div>
                    } maxWidth={500} position="right">
                        <div className="text-gray-400 hover:text-gray-600 transition-colors cursor-help">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </Tooltip>
                </div>
                
                <div className="hidden md:block">
                    <OpportunityPipeline
                        opportunities={opportunitiesWithNames}
                        onOpportunityClick={opp => navigate(`/opportunity/${opp.id}`)}
                        user={user}
                        setToast={setToast}
                    />
                </div>
            </div>

            <ReEngagementQueue usersById={usersById} className="mt-12" />
            </>
            )}
            {showBulkEditModal && (
                <BulkEditModal
                    entityType="opportunity"
                    selectedItems={opportunities.filter(o => selectedOppIds.has(o.id))}
                    users={users}
                    user={user}
                    onClose={() => setShowBulkEditModal(false)}
                    onComplete={(msg) => {
                        setShowBulkEditModal(false);
                        setSelectedOppIds(new Set());
                        setToast({ type: 'success', message: msg });
                    }}
                />
            )}
        </main>
    );
}
export default OppPage;

// helper: escape HTML and convert plain text to simple HTML
const escapeHtml = (str) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const autoLink = (text) =>
  // basic URL -> anchor conversion
  text.replace(
    /((https?:\/\/|www\.)[^\s<]+)/gi,
    (url) => {
      const href = url.startsWith('http') ? url : `http://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    }
  );

const textToHtml = (plain) => {
  if (!plain) return '';
  // normalize CRLF to LF
  let t = plain.replace(/\r\n/g, '\n').trim();
  // escape then auto-link
  t = escapeHtml(t);
  t = autoLink(t);
  // convert paragraphs: double newline -> </p><p>
  const paragraphs = t.split(/\n{2,}/).map(p => p.replace(/\n/g, '<br/>'));
  return paragraphs.map(p => `<p>${p}</p>`).join('');
};

function isPastOrToday(dateStr) {
    if (!dateStr) return false;
    
    // Handle Firestore Timestamp objects
    let selected;
    if (dateStr?.toDate && typeof dateStr.toDate === 'function') {
        selected = dateStr.toDate();
    } else if (typeof dateStr === 'string') {
        selected = new Date(dateStr + 'T00:00:00');
    } else if (dateStr instanceof Date) {
        selected = new Date(dateStr);
    } else {
        return false;
    }
    
    const today = new Date();
    today.setHours(0,0,0,0);
    selected.setHours(0,0,0,0);
    return selected <= today;
}




